// src/utils/templateMeta.js — regras de template da Meta, em funções puras.
//
// Tudo aqui é sem React e sem rede de propósito: é a mesma lógica usada pelo
// formulário de criação, pelo de edição e pelo preview, e função pura é o que
// garante que os três concordem.
//
// ⚠️ ESTE ARQUIVO NÃO É A AUTORIDADE. Quem monta o payload que vai pra Meta é
// o backend (app/routers/templates.py). O que está aqui serve pra dar retorno
// imediato enquanto a pessoa digita, em vez de só descobrir o erro depois do
// POST, e pra desenhar o preview.
//
// Out/26: havia também um espelho do montador do backend, que alimentava um
// painel "ver o JSON". O painel saiu a pedido, e o espelho saiu junto — código
// que duplica regra do servidor sem ninguém olhar só serve pra divergir em
// silêncio. Agora o backend é a única fonte do payload.
//
// Os limites abaixo repetem os de templates.py. Ao mexer num, mexa no outro —
// e lembre que quem recusa de verdade é o backend.

export const LIMITS = {
  BODY: 1024,
  HEADER_TEXT: 60,
  FOOTER: 60,
  BUTTON_TEXT: 25,
  QUICK_REPLIES: 3,
  CTA: 2,
  PHONES: 1,
};

// Regex criada a cada uso, nunca compartilhada: uma regex /g guardada em
// const carrega `lastIndex` entre chamadas e passa a pular ocorrências na
// segunda execução. Bug clássico e silencioso.
const varRegex = () => /\{\{\s*(\d+)\s*\}\}/g;

/** Números das variáveis do texto, em ordem de aparição e sem repetir. */
export function extractVars(text) {
  const found = [];
  for (const match of String(text || '').matchAll(varRegex())) {
    const number = Number(match[1]);
    if (!found.includes(number)) found.push(number);
  }
  return found;
}

/** true quando as variáveis são exatamente 1..n, que é o que a Meta exige. */
export function varsAreSequential(vars) {
  if (!vars.length) return true;
  const sorted = [...vars].sort((a, b) => a - b);
  return sorted.every((value, index) => value === index + 1);
}

/**
 * Renumera as variáveis para 1..n mantendo a ordem numérica, e leva os
 * exemplos junto.
 *
 * Só roda quando a pessoa clica em "Renumerar" — nunca enquanto ela digita.
 * Reescrever o texto debaixo do cursor mexe na posição dele e faz a pessoa
 * perder o lugar no meio de uma frase.
 */
export function renumberVars(text, examples = {}) {
  const ordered = [...extractVars(text)].sort((a, b) => a - b);
  const mapping = new Map(ordered.map((from, index) => [from, index + 1]));

  const nextText = String(text || '').replace(varRegex(), (match, digits) => {
    const to = mapping.get(Number(digits));
    return to ? `{{${to}}}` : match;
  });

  const nextExamples = {};
  mapping.forEach((to, from) => {
    if (examples[from] !== undefined) nextExamples[to] = examples[from];
  });

  return { text: nextText, examples: nextExamples };
}

/** Insere a próxima variável livre na posição do cursor. */
export function insertVariableAt(text, cursorStart, cursorEnd) {
  const source = String(text || '');
  const vars = extractVars(source);
  const next = vars.length ? Math.max(...vars) + 1 : 1;
  const token = `{{${next}}}`;
  const before = source.slice(0, cursorStart);
  const after = source.slice(cursorEnd);
  return { text: `${before}${token}${after}`, token, caret: cursorStart + token.length };
}

/**
 * Troca {{n}} pelo exemplo, para o preview.
 *
 * Variável sem exemplo continua aparecendo como {{n}} em vez de sumir: um
 * buraco no texto esconderia justamente o campo que falta preencher.
 */
export function fillVars(text, examples = {}) {
  return String(text || '').replace(varRegex(), (match, digits) => {
    const value = examples[Number(digits)];
    return value ? String(value) : match;
  });
}

/** Exemplos na ordem que a Meta espera: índice 0 = {{1}}. */
export function orderedExamples(text, examples = {}) {
  return [...extractVars(text)]
    .sort((a, b) => a - b)
    .map((number) => String(examples[number] ?? '').trim());
}

// ─── Validação ────────────────────────────────────────────────────────────
// Devolve uma lista de { field, message }. O componente filtra por `field`
// para mostrar cada erro ao lado do campo que o causou, em vez de despejar
// tudo num alerta no topo — quem está preenchendo precisa saber ONDE.

export function validateTemplate(form) {
  const errors = [];
  const push = (field, message) => errors.push({ field, message });

  if (form.mode !== 'edit') {
    if (!form.name) push('name', 'Dê um nome ao template.');
    else if (!/^[a-z0-9_]+$/.test(form.name)) {
      push('name', 'Use apenas letras minúsculas, números e _ (underscore).');
    }
    if (!form.deviceId) push('deviceId', 'Escolha o chip em que o template será criado.');
  }

  const body = (form.body || '').trim();
  if (!body) push('body', 'A mensagem é obrigatória.');
  if (body.length > LIMITS.BODY) {
    push('body', `A mensagem tem ${body.length} caracteres; o limite é ${LIMITS.BODY}.`);
  }

  const bodyVars = extractVars(body);
  if (bodyVars.length) {
    if (!varsAreSequential(bodyVars)) {
      const achado = [...bodyVars].sort((a, b) => a - b).map((n) => `{{${n}}}`).join(', ');
      push('vars', `As variáveis precisam ser 1, 2, 3… sem pular. Encontrei: ${achado}.`);
    }
    const faltando = bodyVars.filter((n) => !String(form.examples?.[n] ?? '').trim());
    if (faltando.length) {
      const lista = faltando.sort((a, b) => a - b).map((n) => `{{${n}}}`).join(', ');
      push('vars', `Preencha o exemplo de ${lista}. A Meta não aprova sem isso.`);
    }
    if (!body.replace(varRegex(), '').trim()) {
      push('body', 'A mensagem não pode ser só variáveis — escreva algum texto.');
    }
    if (/\}\}\s*\{\{/.test(body)) {
      push('body', 'Duas variáveis não podem ficar coladas; separe com texto.');
    }
  }

  if (form.header?.type === 'TEXT') {
    const headerText = (form.header.text || '').trim();
    if (headerText.length > LIMITS.HEADER_TEXT) {
      push('header', `O cabeçalho passa de ${LIMITS.HEADER_TEXT} caracteres.`);
    }
    const headerVars = extractVars(headerText);
    if (headerVars.length > 1) {
      push('header', 'O cabeçalho aceita no máximo uma variável.');
    }
    if (headerVars.length === 1 && !(form.headerExample || '').trim()) {
      push('header', 'A variável do cabeçalho precisa de um exemplo.');
    }
  }
  if (form.header?.type === 'IMAGE' && !form.header.imageUrl) {
    push('header', 'Envie a imagem ou troque o cabeçalho para "Nenhum".');
  }

  const footer = form.footer || '';
  if (footer.length > LIMITS.FOOTER) {
    push('footer', `O rodapé passa de ${LIMITS.FOOTER} caracteres.`);
  }
  if (extractVars(footer).length) {
    push('footer', 'O rodapé não aceita variáveis.');
  }

  errors.push(...validateButtons(form));
  return errors;
}

function validateButtons(form) {
  const errors = [];
  const push = (message) => errors.push({ field: 'buttons', message });

  // Botão de formulário (Formulários PR 3, out/26): um só, sozinho no
  // template — mesma regra de templates.py._validate_buttons.
  if (form.buttonMode === 'FORM') {
    const botao = form.flowButton || {};
    if (!botao.flowId) push('Escolha o formulário que o botão abre.');
    const text = (botao.text || '').trim();
    if (!text) push('Escreva o texto do botão.');
    else if (text.length > LIMITS.BUTTON_TEXT) push(`O botão passa de ${LIMITS.BUTTON_TEXT} caracteres.`);
  }

  if (form.buttonMode === 'QUICK_REPLY') {
    const filled = (form.quickReplies || []).filter((b) => (b.text || '').trim());
    if (filled.length > LIMITS.QUICK_REPLIES) {
      push(`No máximo ${LIMITS.QUICK_REPLIES} respostas rápidas.`);
    }
    (form.quickReplies || []).forEach((button, index) => {
      const text = (button.text || '').trim();
      if (!text) push(`O botão ${index + 1} está sem texto.`);
      else if (text.length > LIMITS.BUTTON_TEXT) {
        push(`O botão "${text.slice(0, 12)}…" passa de ${LIMITS.BUTTON_TEXT} caracteres.`);
      }
    });
    if (hasDuplicateLabels(filled)) push('Cada botão precisa de um texto diferente.');
  }

  if (form.buttonMode === 'CTA') {
    const ctas = form.ctas || [];
    if (ctas.length > LIMITS.CTA) push(`No máximo ${LIMITS.CTA} botões de ação.`);
    if (ctas.filter((c) => c.type === 'PHONE_NUMBER').length > LIMITS.PHONES) {
      push(`No máximo ${LIMITS.PHONES} botão de telefone.`);
    }
    if (hasDuplicateLabels(ctas)) push('Cada botão precisa de um texto diferente.');

    ctas.forEach((cta, index) => {
      const label = (cta.text || '').trim() || `Botão ${index + 1}`;
      if (!(cta.text || '').trim()) push(`O botão ${index + 1} está sem texto.`);
      else if (cta.text.trim().length > LIMITS.BUTTON_TEXT) {
        push(`O botão "${label}" passa de ${LIMITS.BUTTON_TEXT} caracteres.`);
      }

      if (cta.type === 'URL') {
        const url = (cta.url || '').trim();
        if (!url) push(`Informe o link do botão "${label}".`);
        else if (!/^https?:\/\//i.test(url)) {
          push(`O link de "${label}" precisa começar com https://.`);
        } else {
          const urlVars = extractVars(url);
          if (urlVars.length > 1) push(`O link de "${label}" aceita no máximo uma variável.`);
          if (urlVars.length && !url.trimEnd().endsWith('}}')) {
            push(`Em "${label}", a variável só pode ficar no fim do link.`);
          }
          if (urlVars.length && !(cta.urlExample || '').trim()) {
            push(`O link dinâmico de "${label}" precisa de um exemplo.`);
          }
        }
      }

      if (cta.type === 'PHONE_NUMBER') {
        const phone = (cta.phone || '').trim();
        if (!phone) push(`Informe o telefone do botão "${label}".`);
        else if (!/^\+?[1-9]\d{7,14}$/.test(phone.replace(/[\s()-]/g, ''))) {
          push(`O telefone de "${label}" deve ser internacional, ex.: +5521999999999.`);
        }
      }
    });
  }

  return errors;
}

function hasDuplicateLabels(buttons) {
  const labels = buttons.map((b) => (b.text || '').trim().toLowerCase()).filter(Boolean);
  return new Set(labels).size !== labels.length;
}

// ─── Conversões ───────────────────────────────────────────────────────────

/** Botões do formulário → formato do backend (que monta o payload da Meta). */
export function formButtonsToApi(form) {
  if (form.buttonMode === 'FORM') {
    // Só o id do formulário NA CETTO: o id da Meta o servidor busca e confere.
    const botao = form.flowButton || {};
    return [{ type: 'FLOW', text: (botao.text || '').trim(), flow_id: botao.flowId || null }];
  }
  if (form.buttonMode === 'QUICK_REPLY') {
    return (form.quickReplies || [])
      .filter((b) => (b.text || '').trim())
      .map((b) => ({ type: 'QUICK_REPLY', text: b.text.trim() }));
  }
  if (form.buttonMode === 'CTA') {
    return (form.ctas || []).map((cta) =>
      cta.type === 'URL'
        ? {
            type: 'URL',
            text: (cta.text || '').trim(),
            url: (cta.url || '').trim(),
            url_example: (cta.urlExample || '').trim() || null,
          }
        : {
            type: 'PHONE_NUMBER',
            text: (cta.text || '').trim(),
            phone_number: (cta.phone || '').trim().replace(/[\s()-]/g, ''),
          }
    );
  }
  return [];
}

/** Estado do formulário → corpo do POST /templates/create|edit. */
export function toApiPayload(form) {
  const headerType = form.header?.type || 'NONE';
  return {
    header_type: headerType,
    header_text: headerType === 'TEXT' ? (form.header.text || '').trim() : null,
    header_example: headerType === 'TEXT' ? (form.headerExample || '').trim() || null : null,
    image_url: headerType === 'IMAGE' ? form.header.imageUrl || null : null,
    body: (form.body || '').trim(),
    body_examples: orderedExamples(form.body, form.examples),
    footer: (form.footer || '').trim(),
    buttons: formButtonsToApi(form),
  };
}

/** Template vindo de /templates/list → estado inicial do formulário. */
export function templateToForm(tpl) {
  const buttons = tpl.buttons || [];
  const quickReplies = buttons.filter((b) => b.type === 'QUICK_REPLY');
  const ctas = buttons.filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
  const fluxo = buttons.find((b) => b.type === 'FLOW');

  let buttonMode = 'NONE';
  if (fluxo) buttonMode = 'FORM';
  else if (quickReplies.length) buttonMode = 'QUICK_REPLY';
  else if (ctas.length) buttonMode = 'CTA';

  // body_examples chega como lista posicional (índice 0 = {{1}}); o
  // formulário trabalha com mapa por número da variável.
  const examples = {};
  [...extractVars(tpl.body || '')]
    .sort((a, b) => a - b)
    .forEach((number, index) => {
      const value = (tpl.body_examples || [])[index];
      if (value !== undefined) examples[number] = value;
    });

  return {
    name: tpl.name || '',
    category: tpl.category || 'MARKETING',
    language: tpl.language || 'pt_BR',
    deviceId: '',
    header: {
      type: tpl.header_type || 'NONE',
      text: tpl.header_text || '',
      imageUrl: tpl.image_url || '',
    },
    headerExample: tpl.header_example || '',
    body: tpl.body || '',
    examples,
    footer: tpl.footer || '',
    buttonMode,
    quickReplies: quickReplies.map((b, i) => ({ id: `qr-${i}`, text: b.text || '' })),
    ctas: ctas.map((b, i) => ({
      id: `cta-${i}`,
      type: b.type,
      text: b.text || '',
      url: b.url || '',
      urlExample: b.url_example || '',
      phone: b.phone_number || '',
    })),
    flowButton: fluxo
      ? { flowId: fluxo.flow_id || '', text: fluxo.text || '', flowName: fluxo.flow_name || '' }
      : { ...BOTAO_DE_FORMULARIO },
  };
}

// Botão de formulário recém-escolhido (o texto pode ser trocado).
const BOTAO_DE_FORMULARIO = { flowId: '', text: 'Abrir formulário', flowName: '' };

/** Estado vazio, usado ao abrir a aba de criação e ao limpar o formulário. */
export function emptyForm(deviceId = '') {
  return {
    name: '',
    category: 'MARKETING',
    language: 'pt_BR',
    deviceId,
    header: { type: 'NONE', text: '', imageUrl: '' },
    headerExample: '',
    body: '',
    examples: {},
    footer: '',
    buttonMode: 'NONE',
    quickReplies: [],
    ctas: [],
    flowButton: { ...BOTAO_DE_FORMULARIO },
  };
}
